import { useMemo } from "react";
import { PhaseBar } from "../components/PhaseBar";
import { FLOW_NODES as NODES, PHASE_NAMES } from "../api/constants/status";
import { formatDuration, formatCost } from "../api/utils/format";
import {
  useStatusSummary,
  useAggregates,
  useUnblockedTaskIds,
} from "../api/context/ProjectDashboardContext";

// Layout — all terminals on the right, no crossing:
//                                                    ┌→ DONE ──────┐
// START → PENDING → SETUP → READY → WAITING → RUN ──├→ FAILED      ├→ END
//    │                                               ├→ HUMAN       │
//    ├───────────────────────────────────────────────→ DEFERRED ────┤
//    └───────────────────────────────────────────────→ SKIPPED ─────┘
const NW = 95;
const NH = 22;
const GAP = 16;
const ROW_GAP = 6;
const MAIN_Y = (NH + ROW_GAP); // main row

function nodePos(idx: number): { x: number; y: number } {
  // Main linear flow (0=start..5=running): columns 0-5, main row
  if (idx <= 5) return { x: idx * (NW + GAP), y: MAIN_Y };
  // Right column terminals (6=done, 7=failed, 8=human, 9=deferred, 10=skipped): col 6, stacked
  if (idx <= 10) {
    const row = idx - 6; // 0=done, 1=failed, 2=human, 3=deferred, 4=skipped
    return { x: 6 * (NW + GAP), y: row * (NH + ROW_GAP) };
  }
  // 11=end: col 7, centered vertically among the 5 terminal rows
  return { x: 7 * (NW + GAP), y: 2 * (NH + ROW_GAP) }; // aligned with HUMAN (middle)
}

const EDGES: [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], // linear: start→pending→setup→ready→waiting→running
  [5, 6], [5, 7], [5, 8],                   // right branch from running → done/failed/human
  [1, 9], [1, 10],                           // pending → deferred/skipped
  [6, 11], [9, 11], [10, 11],               // done/deferred/skipped → end
];

/**
 * StatusHeader — migrated to ProjectDashboardContext (T328).
 * Zero direct useQuery calls — all data from context selectors.
 * Cost display: totalCostUsd + costPerSuccessfulTask from aggregates.
 */
export function StatusHeader() {
  const statusState = useStatusSummary();
  const aggregatesState = useAggregates();
  const unblockedState = useUnblockedTaskIds();

  // Unwrap QueryState — show loading skeleton when any selector is loading
  const statusSummary = statusState.status === "ready" ? statusState.data : null;
  const aggregates = aggregatesState.status === "ready" ? aggregatesState.data : null;
  const unblockedIds = unblockedState.status === "ready" ? unblockedState.data : null;

  // Derive stats from aggregates (same shape as old code for display)
  const stats = useMemo(() => {
    if (!aggregates) return null;
    return {
      totalExecutions: aggregates.totalExecutions,
      completedExecutions: aggregates.completedExecutions,
      failedExecutions: aggregates.failedExecutions,
      avgDurationMs: aggregates.avgDurationMs,
      failureRate: aggregates.failureRate,
    };
  }, [aggregates]);

  const readyCount = unblockedIds?.size ?? 0;

  const counts: Record<string, number> = useMemo(() => {
    if (!statusSummary) return {} as Record<string, number>;
    const bs = statusSummary.byStatus;
    const complete = bs.complete ?? 0;
    const deferred = bs.deferred ?? 0;
    const setup = bs.setup ?? 0;
    const waiting = bs.waiting ?? 0;
    const inProgress = bs.in_progress ?? 0;
    const failed = bs.failed ?? 0;
    const needsHuman = bs.needs_human ?? 0;
    const skipped = bs.skipped ?? 0;
    const pending = Math.max(0, statusSummary.total - complete - deferred - setup - waiting - inProgress - failed - needsHuman - skipped - readyCount);
    return {
      _start: statusSummary.total,
      pending,
      setup,
      ready: readyCount,
      waiting,
      in_progress: inProgress,
      complete,
      failed,
      needs_human: needsHuman,
      deferred,
      skipped,
      _end: complete + deferred + skipped,
    };
  }, [statusSummary, readyCount]);

  if (!statusSummary)
    return <div className="h-16 animate-pulse bg-zinc-900 rounded-lg" />;

  // Derive completionPct from byStatus
  const doneCount = (statusSummary.byStatus.complete ?? 0) + (statusSummary.byStatus.deferred ?? 0);
  const pct = statusSummary.total > 0 ? Math.round((doneCount / statusSummary.total) * 100) : 0;

  // Derive phase breakdown from byPhase for PhaseBar
  const phases = Object.entries(statusSummary.byPhase)
    .map(([phaseStr, data]) => {
      const phase = Number(phaseStr);
      return {
        phase,
        name: PHASE_NAMES[phase] ?? `PHASE${phase}`,
        total: data.total,
        complete: data.done,
      };
    })
    .sort((a, b) => a.phase - b.phase);

  // Compute positions
  const positions = NODES.map((_, i) => nodePos(i));
  const svgW = positions.reduce((max, p) => Math.max(max, p.x + NW), 0);
  const svgH = positions.reduce((max, p) => Math.max(max, p.y + NH), 0);

  // Status pills data (skip terminals)
  const STATUS_PILLS = NODES.filter(n => !n.key.startsWith("_"));

  return (
    <div className="space-y-3">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                background: pct === 100 ? "#22c55e" : "linear-gradient(90deg, #6366f1, #818cf8)",
              }}
            />
          </div>
        </div>
        <span className="text-sm font-bold tabular-nums shrink-0">{pct}%</span>
        {stats && stats.totalExecutions > 0 && (
          <>
            <span className="w-px h-3 bg-zinc-700 hidden sm:block" />
            <span className="text-[11px] text-zinc-500 hidden sm:inline">
              {stats.totalExecutions} execs
              {stats.failedExecutions > 0 && <span className="text-red-400"> · {stats.failureRate}% fail</span>}
              {stats.avgDurationMs > 0 && <> · avg {formatDuration(stats.avgDurationMs)}</>}
            </span>
          </>
        )}
        {/* Cost display from aggregates */}
        {aggregates?.totalCostUsd != null && aggregates.totalCostUsd > 0 ? (
          <>
            <span className="w-px h-3 bg-zinc-700 hidden sm:block" />
            <span className="text-[11px] text-zinc-500 hidden sm:inline">
              {formatCost(aggregates.totalCostUsd)}
              {aggregates.costPerSuccessfulTask != null && (
                <span className="text-zinc-600"> · {formatCost(aggregates.costPerSuccessfulTask)}/task</span>
              )}
            </span>
          </>
        ) : (
          <>
            <span className="w-px h-3 bg-zinc-700 hidden sm:block" />
            <span className="text-[11px] text-zinc-600 hidden sm:inline">{"\u2014"}</span>
          </>
        )}
      </div>

      {/* Mobile: compact status pill grid */}
      <div className="sm:hidden flex flex-wrap gap-1.5">
        {STATUS_PILLS.map((node) => {
          const count = counts[node.key] ?? 0;
          if (count === 0) return null;
          return (
            <span
              key={node.key}
              className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded border"
              style={{ color: node.color, backgroundColor: node.bg, borderColor: node.color + "44" }}
            >
              <span>{node.icon}</span>
              <span className="font-semibold text-[10px]">{node.label}</span>
              <span className="font-bold">{count}</span>
            </span>
          );
        })}
      </div>

      {/* Desktop: SVG flow diagram */}
      <div className="hidden sm:block overflow-x-auto" style={{ maxWidth: svgW + 4 }}>
        <svg
          width="100%"
          viewBox={`-2 -2 ${svgW + 4} ${svgH + 4}`}
          style={{ display: "block" }}
        >
          <defs>
            <marker id="flow-arr" viewBox="0 0 6 6" refX="6" refY="3" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M0 0.5 L6 3 L0 5.5z" fill="#3f3f46" />
            </marker>
            <marker id="flow-arr-active" viewBox="0 0 6 6" refX="6" refY="3" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M0 0.5 L6 3 L0 5.5z" fill="#71717a" />
            </marker>
          </defs>

          {/* Edges */}
          {EDGES.map(([from, to]) => {
            const fp = positions[from];
            const tp = positions[to];

            const fromTerminal = NODES[from].key === "_start" || NODES[from].key === "_end";
            const toTerminal = NODES[to].key === "_start" || NODES[to].key === "_end";
            const r = NH / 2 - 1;
            const x1 = fromTerminal ? fp.x + NW / 2 + r : fp.x + NW;
            const y1 = fp.y + NH / 2;
            const x2 = toTerminal ? tp.x + NW / 2 - r : tp.x;
            const y2 = tp.y + NH / 2;

            let d: string;
            const isSameRow = Math.abs(y1 - y2) < 2;
            if (isSameRow) {
              d = `M${x1} ${y1} L${x2} ${y2}`;
            } else {
              const midX = x1 + (x2 - x1) * 0.5;
              const r = 4;
              const dy = y2 > y1 ? 1 : -1;
              d = `M${x1} ${y1} L${midX - r} ${y1} Q${midX} ${y1}, ${midX} ${y1 + r * dy} L${midX} ${y2 - r * dy} Q${midX} ${y2}, ${midX + r} ${y2} L${x2} ${y2}`;
            }

            return (
              <path
                key={`${from}-${to}`}
                d={d}
                fill="none"
                stroke="#52525b"
                strokeWidth={1}
                markerEnd="url(#flow-arr-active)"
                opacity={0.7}
              />
            );
          })}

          {/* Nodes */}
          {NODES.map((node, i) => {
            const pos = positions[i];
            const count = counts[node.key] ?? 0;
            const isTerminal = node.key === "_start" || node.key === "_end";

            if (isTerminal) {
              const cx = pos.x + NW / 2;
              const cy = pos.y + NH / 2;
              const r = NH / 2 - 1;
              return (
                <g key={node.key}>
                  <circle
                    cx={cx} cy={cy} r={r}
                    fill={node.bg}
                    stroke={node.color + "88"}
                    strokeWidth={1.5}
                  />
                </g>
              );
            }

            return (
              <g key={node.key}>
                <rect
                  x={pos.x} y={pos.y}
                  width={NW} height={NH}
                  rx={4}
                  fill={node.bg}
                  stroke={node.color + "66"}
                  strokeWidth={0.75}
                />
                <text
                  x={pos.x + 10} y={pos.y + NH / 2}
                  textAnchor="middle" dy="0.35em"
                  fontSize={8} fontFamily="'Roboto Mono', monospace"
                  fill={node.color}
                >
                  {node.icon}
                  {node.key === "in_progress" && (counts.in_progress ?? 0) > 0 && (
                    <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
                  )}
                </text>
                <text
                  x={pos.x + 20}
                  y={pos.y + NH / 2}
                  textAnchor="start" dy="0.35em"
                  fontSize={9} fontWeight={600} fontFamily="'Roboto Mono', monospace"
                  fill={node.color}
                >
                  {node.label}
                </text>
                <text
                  x={pos.x + NW - 8} y={pos.y + NH / 2}
                  textAnchor="middle" dy="0.35em"
                  fontSize={11} fontWeight={800} fontFamily="'Roboto Mono', monospace"
                  fill={node.color}
                >
                  {count}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* FR-A07: Per-phase progress bars — derived from byPhase, zero additional queries */}
      {phases.length > 0 && (
        <PhaseBar phases={phases} />
      )}
    </div>
  );
}
